import React from 'react';
import type { ComplianceVerdict } from '../../types/scanner';

interface ScoreGaugeProps {
  score: number;
  verdict: ComplianceVerdict;
  size?: 'sm' | 'md' | 'lg';
}

export const ScoreGauge: React.FC<ScoreGaugeProps> = ({ score, verdict, size = 'md' }) => {
  const percentage = Math.round(score);
  const circumference = 2 * Math.PI * 45;
  const offset = circumference - (percentage / 100) * circumference;

  const sizes = {
    sm: { width: 96, fontSize: '1.25rem', labelSize: '0.75rem' },
    md: { width: 160, fontSize: '2.5rem', labelSize: '0.875rem' },
    lg: { width: 224, fontSize: '3.5rem', labelSize: '1rem' },
  };

  const colors = {
    PASS: { stroke: 'var(--color-success)', text: 'var(--color-success)' },
    WARN: { stroke: '#eab308', text: '#eab308' },
    FAIL: { stroke: 'var(--color-error)', text: 'var(--color-error)' },
  };

  const { width, fontSize, labelSize } = sizes[size];
  const { stroke, text } = colors[verdict];

  return (
    <div style={{ position: 'relative', width, height: width }}>
      <svg width="100%" height="100%" viewBox="0 0 100 100">
        {/* Background circle */}
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          strokeWidth="8"
          stroke="#e5e5e5"
        />
        {/* Progress circle */}
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          strokeWidth="8"
          strokeLinecap="square"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          stroke={stroke}
          style={{
            transform: 'rotate(-90deg)',
            transformOrigin: '50% 50%',
            transition: 'stroke-dashoffset 1s ease-out',
          }}
        />
      </svg>
      {/* Center content */}
      <div style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <span style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 700,
          fontSize,
          color: text,
        }}>
          {score}
        </span>
        <span style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          fontSize: labelSize,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: text,
        }}>
          {verdict}
        </span>
      </div>
    </div>
  );
};

export default ScoreGauge;
